require("./threepl-services-table.scss");

import {ChangeDetectionStrategy, Component, computed, inject, input, output, viewChild} from "@angular/core";
import {BreakpointObserver} from "@angular/cdk/layout";
import {CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs";
import {BzTableColumnSettings} from "@bringoz/ui";
import {bzIconSort} from "@bringoz/ui/icons";
import {ThreePLServiceDetails} from "../../models/threepl-service-details.model";
import type {
  ThreePLServicesDynamicColumn, ThreePLServicesSortName, ThreePLServicesSortValue, ThreePLServicesTableLayout,
} from "../../models/threepl-services-list.model";
import {ServiceActionEvent} from "../threepl-service-actions/threepl-service-actions.component";
import {MEDIA_GT_XS} from "../../../../commons/angular/constants/media-breakpoints.constant";
import {
  THREE_PL_SERVICES_MOBILE_FETCH_AHEAD, THREE_PL_SERVICES_SKELETON_ROWS_FIRST_PAGE, THREE_PL_SERVICES_SKELETON_ROWS_NEXT_PAGE,
  THREE_PL_SERVICES_SORT_ICONS, THREE_PL_SERVICES_TABLE_DEFAULT_WIDTHS, THREE_PL_SERVICES_TABLE_DYNAMIC_COLUMN_WIDTH,
} from "../../constants/threepl-services.constant";

export interface ThreePLServicesTableDynamicColumn extends ThreePLServicesDynamicColumn {
  cls: string;
}

export interface ThreePLServicesSkeletonRow {
  id: string;
  skeleton: true;
}

export type ThreePLServicesTableItem = ThreePLServiceDetails | ThreePLServicesSkeletonRow;

const skeletonRows = (count: number, from: number): ThreePLServicesSkeletonRow[] =>
  Array.from({length: count}, (_, i) => ({id: `__skeleton-${from + i}`, skeleton: true}));

@Component({
  selector: "app-threepl-services-table",
  templateUrl: "./threepl-services-table.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreeplServicesTableComponent {
  readonly items = input.required<ThreePLServiceDetails[]>();
  readonly loading = input(false);
  readonly columns = input<Record<string, boolean>>({});
  readonly dynamicColumns = input<ThreePLServicesDynamicColumn[]>();
  readonly layout = input<Partial<ThreePLServicesTableLayout>>();
  readonly sortType = input<ThreePLServicesSortValue>();
  readonly selectedServiceIds = input<string[]>([]);

  readonly toggleService = output<ThreePLServiceDetails>();
  readonly addNote = output<ServiceActionEvent>();
  readonly moveToInternal = output<ServiceActionEvent>();
  readonly cancel = output<ServiceActionEvent>();
  readonly sortTypeChange = output<ThreePLServicesSortName>();
  readonly fetchNext = output<void>();
  readonly layoutChange = output<ThreePLServicesTableLayout>();

  readonly displayAsItems = toSignal(inject(BreakpointObserver).observe(MEDIA_GT_XS).pipe(map((state) => !state.matches)), {initialValue: false});

  private readonly mobileViewport = viewChild(CdkVirtualScrollViewport);

  readonly selectedIds = computed(() => new Set(this.selectedServiceIds() ?? []));
  readonly enabledDynamicColumns = computed<ThreePLServicesTableDynamicColumn[]>(() => (this.dynamicColumns() ?? [])
    .filter((column) => column.enabled)
    .map((column) => ({...column, cls: `col-meta-${String(column.key).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`})));
  readonly empty = computed(() => !this.loading() && !this.items().length);

  readonly defaultWidths = computed(() => ({
    ...THREE_PL_SERVICES_TABLE_DEFAULT_WIDTHS,
    ...Object.fromEntries(this.enabledDynamicColumns().map((column) => [column.cls, THREE_PL_SERVICES_TABLE_DYNAMIC_COLUMN_WIDTH])),
  }));

  readonly tableSettings = computed<BzTableColumnSettings>(() => ({
    columnSizes: {...(this.layout()?.columnSizes ?? {})},
    columnOrder: [...(this.layout()?.columnOrder ?? [])],
  }));

  readonly tableItems = computed<ThreePLServicesTableItem[]>(() => {
    const items = this.items();
    if (!this.loading()) {
      return items;
    }
    return [...items, ...skeletonRows(items.length ? THREE_PL_SERVICES_SKELETON_ROWS_NEXT_PAGE : THREE_PL_SERVICES_SKELETON_ROWS_FIRST_PAGE, items.length)];
  });

  readonly skeletonCards = computed(() => (this.loading() && !this.items().length ? skeletonRows(4, 0) : []));

  readonly sortIcons = computed(() => {
    const sort = this.sortType();
    const iconFor = (name: ThreePLServicesSortName) => (sort?.name === name ? THREE_PL_SERVICES_SORT_ICONS[sort.type] : bzIconSort);
    return {sortEta: iconFor("sortEta"), sortCreatedAt: iconFor("sortCreatedAt")};
  });

  readonly trackById = (index: number, service: ThreePLServiceDetails): string => service.id;

  onLayoutChange(settings: BzTableColumnSettings): void {
    this.layoutChange.emit({columnSizes: {...settings.columnSizes}, columnOrder: [...settings.columnOrder]});
  }

  onMobileScrolled(): void {
    const viewport = this.mobileViewport();
    if (viewport && !this.loading() && viewport.getRenderedRange().end >= this.items().length - THREE_PL_SERVICES_MOBILE_FETCH_AHEAD) {
      this.fetchNext.emit();
    }
  }
}
